import { supabase } from "@/services/supabase/client";
import { Database } from "@/services/supabase/types";
import { loadKeyFromDB } from "@/features/encryption/utils/storage";
import { processTagName, prepareEncryptedTagName } from "../utils/tag-encryption";
import { TagColor } from "@/features/tags/types";

type DbTags = Database["public"]["Tables"]["tags"];
export interface TagRow extends Omit<DbTags["Row"], "color"> {
  color: TagColor | null;
}
export interface TagInsert extends Omit<DbTags["Insert"], "color"> {
  color?: TagColor | null;
}
export interface TagUpdate extends Omit<DbTags["Update"], "color"> {
  color?: TagColor | null;
}

type NoteTags = Database["public"]["Tables"]["note_tags"];
export type NoteTagRow = NoteTags["Row"];
export type NoteTagInsert = NoteTags["Insert"];
export type NoteTagUpdate = NoteTags["Update"];

export interface FetchedTag extends Pick<TagRow, "id" | "color" | "name"> {
  encrypted_data: string | null;
  iv: string | null;
}

async function getTags(): Promise<FetchedTag[]> {
  const { data, error } = await supabase
    .from("tags")
    .select("id, name, color, encrypted_data, iv")
    .order("name", { ascending: true });

  if (error) throw error;

  const dek = await loadKeyFromDB();

  return Promise.all(
    data.map(async (tag) => {
      const name = await processTagName(tag, dek);
      return {
        ...tag,
        color: tag.color as TagColor | null,
        name,
      };
    })
  );
}

async function addTag(tag: TagInsert): Promise<FetchedTag> {
  const dek = await loadKeyFromDB();
  const { encrypted_data, iv, clearName } = await prepareEncryptedTagName(tag.name, dek);

  const insertData = {
    ...tag,
    ...(clearName && encrypted_data && iv ? { encrypted_data, iv, name: "" } : { encrypted_data: "", iv: "" }),
  };

  const { data, error } = await supabase
    .from("tags")
    .insert(insertData)
    .select("id, color, name, encrypted_data, iv")
    .single();

  if (error) throw error;

  return { ...data, color: data.color as TagColor | null, name: tag.name };
}

async function updateTag({ id, tag }: { id: number; tag: TagUpdate }): Promise<FetchedTag | null> {
  const dek = await loadKeyFromDB();

  // Prepare encryption if name is being updated
  if (tag.name) {
    const { encrypted_data, iv, clearName } = await prepareEncryptedTagName(tag.name, dek);

    if (clearName && encrypted_data && iv) {
      tag.encrypted_data = encrypted_data;
      tag.iv = iv;
      tag.name = "";
    }
  }

  const { data, error } = await supabase
    .from("tags")
    .update(tag)
    .eq("id", id)
    .select("id, color, name, encrypted_data, iv")
    .maybeSingle();

  if (error) throw error;

  return data ? { ...data, color: data.color as TagColor | null } : null;
}

async function deleteTag(id: number) {
  const { data, error } = await supabase.from("tags").delete().eq("id", id);

  if (error) throw error;
  return data;
}

async function getNoteTags(noteId: number): Promise<FetchedTag[]> {
  const { data, error } = await supabase
    .from("note_tags")
    .select(
      `
      tag_id,
      tags(
        id,
        color,
        name,
        encrypted_data,
        iv
      )
    `
    )
    .eq("note_id", noteId)
    .order("tags(name)", { ascending: true });

  if (error) throw error;

  const dek = await loadKeyFromDB();

  return Promise.all(
    data.map(async (item) => {
      const name = await processTagName(item.tags, dek);
      return {
        ...item.tags,
        color: item.tags.color as TagColor | null,
        name,
      };
    })
  );
}

async function addNoteTag(noteId: number, tagId: number): Promise<NoteTagRow> {
  const { data, error } = await supabase
    .from("note_tags")
    .insert([{ note_id: noteId, tag_id: tagId }])
    .select()
    .single();

  if (error) throw error;
  return data;
}

async function removeNoteTag(noteId: number, tagId: number): Promise<NoteTagRow> {
  const { data, error } = await supabase
    .from("note_tags")
    .delete()
    .eq("note_id", noteId)
    .eq("tag_id", tagId)
    .select()
    .single();

  if (error) throw error;
  return data;
}

export const tagsFetchers = {
  getTags,
  addTag,
  updateTag,
  deleteTag,
  getNoteTags,
  addNoteTag,
  removeNoteTag,
};
